import {
  convertDateToYYYYMMDDFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  formatDataToLabelValue,
  FormFeedBack,
  getFirstDayOfMonth,
  getToday,
  IOptions,
  Select,
} from '@repo/ui';
import {
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
} from 'domain/usecases';
import { FormikProps } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useFiltersPermits } from 'presentation/app/hooks/useFiltersPermits';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadCoursesUser } from 'presentation/config/store/courses/courses-slice';
import { loadCurricularUnitsUser } from 'presentation/config/store/curricular-units/curricular-units-slice';
import { loadGroupsUser } from 'presentation/config/store/groups/groups-slice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import { loadSemestersUser } from 'presentation/config/store/semesters/semesters-slice';
import { ReactNode, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CardBody, CardFooter, Input, Col, Form, Label, Row } from 'reactstrap';
import { InputType } from 'reactstrap/types/lib/Input';

interface IProps {
  validation: FormikProps<any>;
  additionalDependentInputs?: IInputs[];
  additionalGeneralInputs?: IInputs[];
  buttonsChildren?: ReactNode;
  populateAdditionalDependentInputs?: () => void;
}

interface IInputs {
  type?: InputType;
  isSelect?: boolean;
  id: string;
  label: string;
  sizes?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  };
  onChange: (e) => void;
  options?: IOptions[];
  placeholder?: string;
  isVisible?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
}

export const GeneralFilters = ({
  validation,
  additionalDependentInputs = [],
  additionalGeneralInputs = [],
  buttonsChildren,
  populateAdditionalDependentInputs,
}: IProps) => {
  // Hooks
  const service = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();
  const {
    filterByCurricularUnit,
    filterBySemester,
    isNotManager,
    filtersDisabled,
    setFiltersDisabled,
  } = useFiltersPermits();
  const {
    groupService,
    courseService,
    semesterService,
    organizationService,
    curricularUnitService,
  } = service;

  // States
  const { coursesUser } = useAppSelector((state) => state.courses);
  const { organizationsUser } = useAppSelector((state) => state.organizations);
  const { semestersUser } = useAppSelector((state) => state.semesters);
  const { curricularUnitsUser } = useAppSelector(
    (state) => state.curricularUnits,
  );
  const { groupsUser } = useAppSelector((state) => state.groups);

  // Methods
  const requestGetAllOrganizationsUser = useCallback(async () => {
    await organizationService
      .getAllOrganizationsUser()
      .then((res) => dispatch(loadOrganizationsUser(res)))
      .catch((err) => notifyError(err));
  }, []);

  const requestGetAllCoursesUser = useCallback(async (entityId?: string) => {
    await courseService
      .getAllCoursesUser({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => notifyError(err));
  }, []);

  const requestGetAllSemestersUser = useCallback(
    async (params: GetAllSemestersUserUseCaseInterface.Params) => {
      await semesterService
        .getAllSemestersUser(params)
        .then((res) => dispatch(loadSemestersUser(res)))
        .catch((err) => notifyError(err));
    },
    [],
  );

  const requestGetAllCurricularUnitsUser = useCallback(
    async (params: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      await curricularUnitService
        .getAllCurricularUnitsUser(params)
        .then((res) => dispatch(loadCurricularUnitsUser(res)))
        .catch((err) => notifyError(err));
    },
    [],
  );

  const requestGetAllGroupsUser = useCallback(
    async (params: GetAllGroupsUserUseCaseInterface.Params) => {
      await groupService
        .getAllGroupsUser(params)
        .then((res) => dispatch(loadGroupsUser(res)))
        .catch((err) => notifyError(err));
    },
    [],
  );

  const populateIndependentInputs = useCallback(() => {
    requestGetAllOrganizationsUser();
    requestGetAllCoursesUser();
  }, []);

  const populateDependentInputs = useCallback(() => {
    const selectedOrganization = validation.values.entity?.value;
    const selectedCourse = validation.values.course?.value;

    const selectedSemester = validation.values.semester?.value || '';
    const selectedCurricularUnit = validation.values.unit?.value || '';

    requestGetAllCurricularUnitsUser({
      entityId: selectedOrganization,
      courseId: selectedCourse,
    });
    requestGetAllGroupsUser({
      courseId: selectedCourse,
      semesterId: selectedSemester,
      entityId: selectedOrganization,
      curricularUnitId: selectedCurricularUnit,
    });
    requestGetAllSemestersUser({ entityId: selectedOrganization });

    if (populateAdditionalDependentInputs) {
      populateAdditionalDependentInputs();
    }
  }, []);

  const verifyDependentInputsAvailability = useCallback(() => {
    const selectedOrganization = validation.values.entity?.value;
    const selectedCourse = validation.values.course?.value;

    if (selectedOrganization && selectedCourse) {
      populateDependentInputs();
      setFiltersDisabled(false);
    } else if (isNotManager) {
      populateDependentInputs();
      setFiltersDisabled(false);
    } else {
      setFiltersDisabled(true);
    }
  }, [validation]);

  const changeIndependentInputs = useCallback(({ key, value }) => {
    validation.setFieldValue('semester', null);
    validation.setFieldValue('unit', null);
    validation.setFieldValue('group', []);

    if (additionalDependentInputs) {
      additionalDependentInputs.forEach(({ id }) => {
        validation.setFieldValue(id, null);
      });
    }

    if (key === 'entity') {
      validation.setFieldValue('course', null);
      requestGetAllCoursesUser(value?.value);
    }
    validation.handleChange(convertEventSelectToTarget(key, value));
  }, []);

  const inputs: { independents: IInputs[]; dependents: IInputs[] } = {
    independents: [
      {
        id: 'entity',
        label: `${getLegends('ORGANIZACAO', false)}:`,
        placeholder: `Todas as ${getLegends('ORGANIZACAO', true)}`,
        onChange: (e) => changeIndependentInputs({ key: 'entity', value: e }),
        options: formatDataToLabelValue(
          organizationsUser as never,
          'id',
          'nome',
        ),
        isVisible: true,
        isSelect: true,
        isMulti: false,
      },
      {
        id: 'course',
        label: `${getLegends('CURSO', false)}:`,
        onChange: (e) => changeIndependentInputs({ key: 'course', value: e }),
        options: formatDataToLabelValue(coursesUser as never, 'id', 'nome'),
        placeholder: `Todos os ${getLegends('CURSO', true)}`,
        isVisible: true,
        isSelect: true,
        isMulti: false,
      },
    ],
    dependents: [
      {
        id: 'unit',
        label: `${getLegends('UNIDADE_CURRICULAR', false)}:`,
        onChange: (e) =>
          validation.handleChange(convertEventSelectToTarget('unit', e)),
        options: formatDataToLabelValue(
          curricularUnitsUser as never,
          'id',
          'nome',
        ),
        placeholder: `Todas as ${getLegends('UNIDADE_CURRICULAR', true)}`,
        isVisible: filterByCurricularUnit,
        isDisabled: filtersDisabled,
        isSelect: true,
        isMulti: false,
      },
      {
        id: 'semester',
        label: `${getLegends('SEMESTRE', false)}:`,
        onChange: (e) =>
          validation.handleChange(convertEventSelectToTarget('semester', e)),
        options: formatDataToLabelValue(semestersUser as never, 'id', 'nome'),
        placeholder: `Todos os ${getLegends('SEMESTRE', true)}`,
        isVisible: filterBySemester,
        isDisabled: filtersDisabled,
        isSelect: true,
        isMulti: false,
      },
      {
        id: 'group',
        label: `${getLegends('GRUPO', false)}:`,
        sizes: {
          md: 8,
        },
        onChange: (e) =>
          validation.handleChange(convertEventSelectToTarget('group', e)),
        options: formatDataToLabelValue(groupsUser as never, 'id', 'nome'),
        placeholder: `Todos os ${getLegends('GRUPO', true)}`,
        isVisible: true,
        isDisabled: filtersDisabled,
        isSelect: true,
        isMulti: true,
      },
    ],
  };

  const mergeDependentInputs = useCallback(
    (baseDependents: IInputs[], newDependents: IInputs[]) => {
      const merged = [...baseDependents];

      newDependents.forEach((newDependent) => {
        const existingIndex = merged.findIndex(
          (dep) => dep.id === newDependent.id,
        );
        if (existingIndex === -1) {
          merged.push(newDependent);
        } else {
          merged[existingIndex] = newDependent;
        }
      });

      return merged;
    },
    [],
  );

  const updatedDependentInputs = mergeDependentInputs(
    inputs.dependents,
    additionalDependentInputs,
  );

  // Verificação para mostrar inputs de data, para evitar conflitos com outros inputs.
  const hasDateRangeInputs =
    additionalGeneralInputs.some(
      (item) => item.id === 'starts' && item.type === 'date',
    ) &&
    additionalGeneralInputs.some(
      (item) => item.id === 'ends' && item.type === 'date',
    );

  useEffect(() => {
    populateIndependentInputs();
  }, []);

  useEffect(() => {
    verifyDependentInputsAvailability();
  }, [validation.values]);

  return (
    <Form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <CardBody>
        <Row>
          {inputs.independents.map(
            (item) =>
              item.isVisible && (
                <Col md={4} className="mb-3" key={item.id}>
                  <Label htmlFor={item.id} className="form-label">
                    {item.label}
                  </Label>
                  <Select
                    isClearable
                    id={item.id}
                    name={item.id}
                    placeholder={item.placeholder}
                    value={validation.values?.[item.id]}
                    onChange={item.onChange}
                    invalid={
                      !!(validation.touched && validation.errors?.[item.id])
                    }
                    onBlur={validation.handleBlur}
                    options={item.options}
                  />
                  <FormFeedBack
                    error={validation.errors?.[item.id]?.toString()}
                    isInvalid={
                      !!(validation.touched && validation.errors?.[item.id])
                    }
                  />
                </Col>
              ),
          )}
          {updatedDependentInputs.map(
            (item) =>
              item.isVisible && (
                <Col
                  xs={item.sizes?.xs}
                  sm={item.sizes?.sm}
                  md={item.sizes?.md || 4}
                  lg={item.sizes?.lg}
                  xl={item.sizes?.xl}
                  xxl={item.sizes?.xxl}
                  className="mb-3"
                  key={item.id}
                >
                  <Label htmlFor={item.id} className="form-label">
                    {item.label}
                  </Label>
                  {item.isSelect ? (
                    <Select
                      isClearable
                      id={item.id}
                      isMulti={item.isMulti ?? false}
                      name={item.id}
                      placeholder={item.placeholder}
                      value={validation.values?.[item.id]}
                      onChange={item.onChange}
                      invalid={
                        !!(
                          validation.touched?.[item.id] &&
                          validation.errors?.[item.id]
                        )
                      }
                      onBlur={validation.handleBlur}
                      options={item.options}
                      isDisabled={item.isDisabled ?? filtersDisabled}
                    />
                  ) : (
                    <Input
                      type={item.type || 'text'}
                      id={item.id}
                      name={item.id}
                      placeholder={item.placeholder}
                      value={validation.values?.[item.id]}
                      onChange={item.onChange}
                      invalid={
                        !!(
                          validation.touched?.[item.id] &&
                          validation.errors?.[item.id]
                        )
                      }
                      onBlur={validation.handleBlur}
                    />
                  )}

                  <FormFeedBack
                    error={validation.errors?.[item.id]?.toString()}
                    isInvalid={
                      !!(
                        validation.touched?.[item.id] &&
                        validation.errors?.[item.id]
                      )
                    }
                  />
                </Col>
              ),
          )}
          {hasDateRangeInputs ? (
            <Col sm={12} md={6} lg={4} className="mb-3">
              <Row>
                <Label htmlFor="initial-date" className="form-label">
                  Período
                </Label>
                <CustomDateRangePicker
                  initialStartDate={getFirstDayOfMonth()}
                  initialEndDate={getToday()}
                  placement="auto"
                  onDateChange={(start, ends) => {
                    validation.setFieldValue(
                      'starts',
                      convertDateToYYYYMMDDFormat(start),
                    );
                    validation.setFieldValue(
                      'ends',
                      convertDateToYYYYMMDDFormat(ends),
                    );
                  }}
                />
              </Row>
            </Col>
          ) : (
            additionalGeneralInputs.map(
              (item) =>
                item.isVisible && (
                  <Col
                    xs={item.sizes?.xs}
                    sm={item.sizes?.sm}
                    md={item.sizes?.md || 4}
                    lg={item.sizes?.lg}
                    xl={item.sizes?.xl}
                    xxl={item.sizes?.xxl}
                    className="mb-3"
                    key={item.id}
                  >
                    <Label htmlFor={item.id} className="form-label">
                      {item.label}
                    </Label>
                    {item.isSelect ? (
                      <Select
                        isClearable
                        id={item.id}
                        isMulti={item.isMulti ?? false}
                        name={item.id}
                        placeholder={item.placeholder}
                        value={validation.values?.[item.id]}
                        onChange={item.onChange}
                        invalid={
                          !!(
                            validation.touched?.[item.id] &&
                            validation.errors?.[item.id]
                          )
                        }
                        onBlur={validation.handleBlur}
                        options={item.options}
                        isDisabled={item.isDisabled ?? filtersDisabled}
                      />
                    ) : (
                      <Input
                        type={item.type || 'text'}
                        id={item.id}
                        name={item.id}
                        placeholder={item.placeholder}
                        value={validation.values?.[item.id]}
                        onChange={item.onChange}
                        invalid={
                          !!(
                            validation.touched?.[item.id] &&
                            validation.errors?.[item.id]
                          )
                        }
                        onBlur={validation.handleBlur}
                      />
                    )}

                    <FormFeedBack
                      error={validation.errors?.[item.id]?.toString()}
                      isInvalid={
                        !!(
                          validation.touched?.[item.id] &&
                          validation.errors?.[item.id]
                        )
                      }
                    />
                  </Col>
                ),
            )
          )}
        </Row>
      </CardBody>
      {buttonsChildren && (
        <CardFooter className="d-flex justify-content-end gap-3">
          {buttonsChildren}
        </CardFooter>
      )}
    </Form>
  );
};
